import React, { useState, useEffect } from 'react';
import LoadingOverlay from '../../loadingOverlay/LoadingOverlay';
import ContactsTable from '../ContactsTable';
import { Clear, Check, EmojiEvents } from '@mui/icons-material';
import { Toolbar, Paper, Table, TableHead, TableBody, TableContainer, TableRow, TableCell, TableSortLabel, Button, Divider, Box, InputAdornment, IconButton, OutlinedInput, Chip } from '@mui/material';
import useSearch from '../../hooks/useSearch';
import { useTheme } from '@mui/material/styles';
import { lighten } from '@mui/system';
import TypedValue from '../../TypedValue';

const emailColumns = [
    { name: 'i', label: '#', orderDesc: false, isSortable: true },
    { name: 'url', label: 'URL', orderDesc: false, minWidth: false, isSortable: false },
    { name: 'uniqueClicks', label: 'Unique clicks', align: 'right', orderDesc: true, minWidth: true, isSortable: true },
    { name: 'totalClicks', label: 'Total clicks', align: 'right', orderDesc: true, minWidth: true, isSortable: true }
];

const pageColumns = [
    { name: 'i', label: '#', orderDesc: false, isSortable: true },
    { name: 'url', label: 'URL', orderDesc: false, minWidth: false, isSortable: false },
    { name: 'totalClicks', label: 'Total clicks', align: 'right', orderDesc: true, minWidth: true, isSortable: true },
    { name: 'active', label: 'Active', align: 'right', orderDesc: false, minWidth: false, isSortable: true }
];

const NewLinks = ({ onViewWhoClicked, linkId, allLinks, onRefreshGroups, type = 'email', id = null }) => {
    const theme = useTheme();
    const [links, setLinks] = useState(null);
    const [unfilteredLinks, setUnfilteredLinks] = useState(null);
    const [orderBy, setOrderBy] = useState('i');
    const [orderDesc, setOrderDesc] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [includeInactive, setIncludeInactive] = useState(type === 'email' ? true : false);
    const [searchValue, setSearchValue] = useState('');
    const [tempSearchValue, setTempSearchValue] = useSearch(setSearchValue);
    const [mostClickedLinks, setMostClickedLinks] = useState(null);

    const handleSortLinks = (a, b) => orderDesc ? b[orderBy] - a[orderBy] : a[orderBy] - b[orderBy];

    const handleSort = (field, defaultOrder) => {
        if (field === orderBy) {
            setOrderDesc(!orderDesc);
        }
        else {
            setOrderBy(field);
            setOrderDesc(defaultOrder);
        }
    };

    const style = {
        minWidth: '150px'
    };

    const handleRowClick = ({ id, hasClicks }) => {
        (hasClicks && onViewWhoClicked) && onViewWhoClicked(id);
    };

    const findMostClickedLinks = links => {
        let maxUniqueClicks = 0;
        let maxTotalClicks = 0;
        let topLinks = [];

        for (const link of links) {
            if (link.uniqueClicks > maxUniqueClicks) {
                maxUniqueClicks = link.uniqueClicks;
                maxTotalClicks = link.totalClicks;
                topLinks = [link];
            }
            else if (link.uniqueClicks === maxUniqueClicks) {
                if (link.totalClicks > maxTotalClicks) {
                    maxTotalClicks = link.totalClicks;
                    topLinks = [link];
                }
                else if (link.totalClicks === maxTotalClicks) {
                    topLinks.push(link);
                }
            }
        }

        if (maxUniqueClicks > 0) {
            setMostClickedLinks(topLinks);
        }
    };

    const handleSetLinks = () => {
        let links = [];

        if (allLinks?.length > 0) {
            links = includeInactive
                ? allLinks.map((link, i) => ({ ...link, i })).sort(handleSortLinks)
                : allLinks.filter(e => e.active).map((link, i) => ({ ...link, i })).sort(handleSortLinks);
        }

        links.length > 1 && findMostClickedLinks(links);

        setLinks(links);
        setUnfilteredLinks(links);
    };

    useEffect(() => {
        handleSetLinks();
    }, [includeInactive, allLinks]);

    useEffect(() => {
        links && setIsLoading(false);
    }, [links]);

    useEffect(() => {
        if (!unfilteredLinks) {
            return;
        }

        let result = unfilteredLinks;

        if (searchValue) {
            result = unfilteredLinks.filter(e => e.url.toLowerCase().includes(searchValue.toLowerCase()));
        }

        setLinks([...result.sort(handleSortLinks)]);
    }, [orderBy, orderDesc, searchValue]);

    useEffect(() => {
        handleSetLinks();
    }, []);

    if (isLoading) {
        return (
            <LoadingOverlay />
        );
    }

    const columns = type === 'email' ? emailColumns : pageColumns;

    return (
        <>
            <Paper sx={{ m: 2 }}>
                <Toolbar disableGutters sx={{ mx: 1 }}>
                    <Box sx={{ flexGrow: 1 }}>
                        <OutlinedInput
                            value={tempSearchValue}
                            onChange={e => setTempSearchValue(e.target.value)}
                            placeholder="Search URL..."
                            size="small"
                            sx={{ m: 1 }}
                            endAdornment={(
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setTempSearchValue('')}
                                        edge="end"
                                        disabled={!Boolean(tempSearchValue)}
                                    >
                                        <Clear />
                                    </IconButton>
                                </InputAdornment>
                            )}
                        />
                    </Box>
                    {type === 'page' && (
                        <Button
                            onClick={() => setIncludeInactive(!includeInactive)}
                            disabled={!allLinks?.some(e => !e.active)}
                        >
                            {includeInactive ? 'Hide' : 'Show'} Inactive Links
                        </Button>
                    )}
                </Toolbar>
                <Divider />
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {columns.map(col => (
                                    <TableCell
                                        key={col.name}
                                        component="th"
                                        scope="row"
                                        align={col.align}
                                        sx={col.minWidth ? style : null}
                                    >
                                        {col.isSortable ? (
                                            <TableSortLabel
                                                active={orderBy === col.name}
                                                direction={orderDesc ? 'desc' : 'asc'}
                                                onClick={() => handleSort(col.name, col.orderDesc)}
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        ) : (
                                            col.label
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {links.map(({ i, id, url, totalClicks, uniqueClicks, active }) => {
                                const hasClicks = totalClicks > 0;
                                const mostClickedLink = mostClickedLinks?.map(e => e.id).includes(id);

                                return (
                                    <TableRow
                                        key={id}
                                        hover={onViewWhoClicked && hasClicks}
                                        onClick={() => handleRowClick({ id, hasClicks })}
                                        selected={parseInt(linkId) === parseInt(id)}
                                        sx={mostClickedLink ? { backgroundColor: lighten(theme.palette.success.light, 0.7) } : undefined}
                                    >
                                        <TableCell>
                                            {(type === 'email' || active) ? (i + 1) : '-'}
                                        </TableCell>
                                        <TableCell
                                            sx={{ wordBreak: 'break-all' }}
                                        >
                                            {url}
                                            {mostClickedLink && (
                                                <Chip
                                                    label="Most Clicked"
                                                    size="small"
                                                    color="success"
                                                    icon={<EmojiEvents fontSize="inherit" />}
                                                    sx={{ ml: 1 }}
                                                />
                                            )}
                                        </TableCell>
                                        {type === 'email' && (
                                            <TableCell align="right" sx={style}>
                                                <TypedValue value={uniqueClicks} accuracy={0} type="number" />
                                            </TableCell>
                                        )}
                                        <TableCell align="right" sx={style}>
                                            <TypedValue value={totalClicks} accuracy={0} type="number" />
                                        </TableCell>
                                        {type === 'page' && (
                                            <TableCell
                                                align="right"
                                                sx={style}
                                            >
                                                {active ? <Check color="success" /> : <Clear color="disabled" />}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            {(type === 'email' && id && linkId) && (
                <Box sx={{ m: 2 }}>
                    <ContactsTable
                        campaignId={id}
                        linkId={linkId}
                        enableJumping={true}
                        onRefreshGroups={onRefreshGroups}
                    />
                </Box>
            )}
        </>
    );
};

export default NewLinks;